import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGxpcXVpZCUyMGZsb3clMjBkYXJrfGVufDB8fHx8MTc4NjM4OTM3OXww&ixlib=rb-4.1.0&q=85&w=1920&auto=format&fit=crop";

const LINES = ["PREMIUM", "RESEARCH", "PEPTIDES"];

const STATS = [
  { value: "99%+ PURITY", label: "HPLC tested with full documentation" },
  { value: "BUY 3+ SAME: 25% OFF", label: "Bulk discount on identical items" },
];

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt=""
          className="h-[125%] w-full object-cover opacity-45 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="mb-6 font-mono text-[11px] tracking-[0.35em] text-[#00ff72]"
        >
          RESEARCH GRADE COMPOUNDS
        </motion.p>

        <h1 className="font-display text-[15vw] leading-[0.88] tracking-tighter text-[#F9F6F0] sm:text-[11vw] lg:text-[9rem]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-[#00ff72]" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.15, ease: [0.76, 0, 0.24, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          className="mt-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-md text-sm leading-relaxed text-[#A3A3A3]">
            Laboratory-grade peptides for cutting-edge research. 99%+ purity guaranteed with full
            third-party documentation on every batch.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/products"
              data-testid="hero-shop-now-btn"
              className="group flex items-center gap-3 bg-[#00ff72] px-8 py-4 font-mono text-xs tracking-[0.2em] text-black transition-colors hover:bg-[#F9F6F0]"
            >
              SHOP NOW
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/products?category=Recovery"
              data-testid="hero-browse-catalog-btn"
              className="flex items-center gap-3 border border-white/25 px-8 py-4 font-mono text-xs tracking-[0.2em] text-[#F9F6F0] transition-colors hover:border-[#00ff72] hover:text-[#00ff72]"
            >
              BROWSE CATALOG
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="mt-16 grid grid-cols-1 gap-6 border-t border-white/10 pt-8 sm:grid-cols-2"
        >
          {STATS.map((s) => (
            <div key={s.value}>
              <p className="font-mono text-sm tracking-wider text-[#F9F6F0]">{s.value}</p>
              <p className="mt-1 text-xs text-[#666666]">{s.label}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="mt-10 flex justify-center"
        >
          <ArrowDown className="h-4 w-4 animate-bounce text-[#666666]" />
        </motion.div>
      </div>
    </section>
  );
}
